import React, { useState } from 'react';
import { UserIcon, EnvelopeSimpleIcon, WalletIcon, BellIcon, MapIcon } from '@phosphor-icons/react';
import axios from 'axios';
import './Account.scss';
import AccountModal from './Modals/AccountModal.js';
import { useNotificationSettings } from '../../../hooks/useNotificationSettings.js';
import { useUserContext } from '../../../contexts/UserContext.js';
import { useFiresContext } from '../../../contexts/FiresContext.js';
import { useAreasContext } from '../../../contexts/AreasContext.js';
import { useSensorDataContext } from '../../../contexts/SensorDataContext.js';
import { useAlertsContext } from '../../../contexts/AlertsContext.js';
import { useFacilitiesContext } from '../../../contexts/FacilitiesContext.js';
import { useTeamsContext } from '../../../contexts/TeamsContext.js';
import mapStreets from '../../../assets/images/map-streets.png';
import mapDark from '../../../assets/images/map-dark.png';
import mapSatelliteStreets from '../../../assets/images/map-satellite-streets.png';
import mapNavDay from '../../../assets/images/map-navigation-day.png';
import mapNavNight from '../../../assets/images/map-navigation-night.png';

export default function Account(props) {
    const { 
        user, 
        config, 
        setPanel,
        getToken,
        map
    } = props;
    
    // Context hooks for re-triggering data loading
    const { getFires } = useFiresContext();
    const { getAreas } = useAreasContext();
    const { getPredictions, getRAWS, getVIIRS, getMODIS, getSatelliteLayers } = useSensorDataContext();
    const { getAlerts } = useAlertsContext();
    const { getPowerPlants, getEmergencyStations } = useFacilitiesContext();
    const { getTeams } = useTeamsContext();

    const { setConfig } = useUserContext();

    const [email, setEmail] = useState(user.idToken.payload.email);

    const [modal, setModal] = useState({
        type: false,
        content: null
    });

    // Notification settings hook
    const {
        emailNotificationsEnabled,
        pushNotificationsEnabled, // Available for reading but not toggling
        loading: notificationsLoading,
        error: notificationsError,
        handleEmailNotificationsToggle
    } = useNotificationSettings(getToken);

    // Map styles data
    const mapStyles = [
        {
            name: 'Satellite Streets',
            link: 'mapbox://styles/mapbox/satellite-streets-v12',
            image: mapSatelliteStreets
        },
        {
            name: 'Dark',
            link: 'mapbox://styles/mapbox/dark-v11',
            image: mapDark
        },
        {
            name: 'Streets',
            link: 'mapbox://styles/mapbox/streets-v12',
            image: mapStreets
        },
        {
            name: 'Navigation Day',
            link: 'mapbox://styles/mapbox/navigation-day-v1',
            image: mapNavDay
        },
        {
            name: 'Navigation Night',
            link: 'mapbox://styles/mapbox/navigation-night-v1',
            image: mapNavNight
        },
    ];

    const handleMapStyleChange = (styleLink) => {
        if (!map || !map.current) return;
        
        try {
            // Store current map state to preserve it
            const currentCenter = map.current.getCenter();
            const currentZoom = map.current.getZoom();
            const currentPitch = map.current.getPitch();
            const currentBearing = map.current.getBearing();
            
            // Set a flag to prevent globe spinning during style change
            map.current._skipGlobeSpin = true;
            
            // Change the map style
            map.current.setStyle(styleLink);
            
            // Wait for the new style to load, then re-add essential layers
            map.current.once('style.load', () => {
                // Restore the map state immediately to prevent globe spinning
                map.current.setCenter(currentCenter);
                map.current.setZoom(currentZoom);
                map.current.setPitch(currentPitch);
                map.current.setBearing(currentBearing);
                // Re-add globe projection
                map.current.setProjection({ name: 'globe' });
                
                // Re-add 3D buildings layer
                const layers = map.current.getStyle().layers;
                const labelLayerId = layers.find(l => l.type === 'symbol' && l.layout && l.layout['text-field']);
                
                if (labelLayerId) {
                    try {
                        map.current.addLayer({
                            id: '3d-buildings',
                            source: 'composite',
                            'source-layer': 'building',
                            filter: ['==', 'extrude', 'true'],
                            type: 'fill-extrusion',
                            minzoom: 15,
                            paint: {
                                'fill-extrusion-color': '#aaa',
                                'fill-extrusion-height': ['interpolate', ['linear'], ['zoom'], 15, 0, 15.05, ['get', 'height']],
                                'fill-extrusion-base': ['interpolate', ['linear'], ['zoom'], 15, 0, 15.05, ['get', 'min_height']],
                                'fill-extrusion-opacity': 0.8
                            }
                        }, labelLayerId.id);
                    } catch (e) {
                        console.warn('Could not add 3D buildings:', e);
                    }
                }
                
                // Re-add DEM source for terrain
                try {
                    map.current.addSource('dem', {
                        type: 'raster-dem',
                        url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
                        tileSize: 512,
                        maxzoom: 14
                    });
                    
                    map.current.setTerrain({ source: 'dem', exaggeration: 1.5 });
                } catch (e) {
                    console.warn('Could not add terrain:', e);
                }
                
                // Re-trigger all data loading to restore layers
                setTimeout(() => {
                    console.log('Re-triggering data loading after style change...');
                    
                    // Re-load fires and satellite data (no auth required)
                    getFires(map);
                    getVIIRS(map);
                    getMODIS(map);
                    
                    // Re-load user-specific data (auth required)
                    if (user) {
                        getToken().then(token => {
                            getAreas(map, token);
                            getPredictions(map, token);
                            getAlerts(token);
                            getSatelliteLayers(token);
                            getTeams(token);
                        });
                        
                        // Re-load RAWS data
                        setTimeout(() => getRAWS(config, map, user), 500);
                    }
                    
                    // Update config after all data is re-plotted
                    setConfig({
                        ...config,
                        map_style: styleLink
                    });
                }, 1000);
            });
            
            const selectedStyle = mapStyles.find(style => style.link === styleLink);
            if (selectedStyle) {
                console.log(`Map style set to ${selectedStyle.name}`);
            }
        } catch (error) {
            console.error('Error changing map style:', error);
        }
    };

    const handleManageSubscription = () => {
          console.log('config', config);
          getToken().then((token) => {
            console.log('token', token);
            axios.post('https://firewatch-api.space-eyes.com/user/manage-stripe-subscription', {
                customerId: config.customerId
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })
            .then((response) => {
                console.log('response', response.data);
                window.location.href = response.data.url;
          }).catch((error) => {
            console.error('Error getting token:', error);
          });
      });
    }

    const renderAccountContent = () => {
        return (
            <div className='account-page-content'>
                {/* Profile Section */}
                <div className='account-page-content-section'>
                    <div className='account-page-content-section-title'>Profile Information</div>
                    <div className='account-page-content-profile'>
                        <div className='account-page-content-profile-row'>
                            <UserIcon size={20} />
                            <div className='account-page-content-profile-row-text'>{user.idToken.payload.name}</div>
                        </div>
                        <div className='account-page-content-profile-row'>
                            <EnvelopeSimpleIcon size={20} />
                            <div className='account-page-content-profile-row-text'>{email}</div>
                        </div>
                        <div className='account-page-content-profile-btns'>
                            {/* <button 
                                onClick={() => {
                                    setModal({
                                        type: 'update-email',
                                        content: null
                                    });
                                }}
                                className='account-page-content-profile-btns-btn'>
                                Update Email
                            </button> */}
                            <button 
                                onClick={() => {
                                    setModal({
                                        type: 'change-password',
                                        content: null
                                    });
                                }}
                                className='account-page-content-profile-btns-btn'>
                                Change Password
                            </button>
                        </div>
                    </div>
                </div>
                {/* Notification Settings Section */}
                <div className='account-page-content-section'>
                    <div className='account-page-content-section-title'>Notification Settings</div>
                    <div className='account-page-content-notifications'>
                        {notificationsLoading ? (
                            <div className='account-page-content-notifications-loading'>
                                Loading notification settings...
                            </div>
                        ) : notificationsError ? (
                            <div className='account-page-content-notifications-error'>
                                {notificationsError}
                            </div>
                        ) : (
                            <div className='account-page-content-notifications-settings'>
                                <div className='account-page-content-notifications-setting'>
                                    <div className='account-page-content-notifications-setting-info'>
                                        <div className='account-page-content-notifications-setting-title'>
                                            <BellIcon size={18} />
                                            Email Notifications
                                        </div>
                                        <div className='account-page-content-notifications-setting-description'>
                                            Receive fire alerts, updates, and important information via email
                                        </div>
                                    </div>
                                    <div className='account-page-content-notifications-toggle'>
                                        <input
                                            type="checkbox"
                                            id="email-notifications"
                                            checked={emailNotificationsEnabled}
                                            onChange={(e) => handleEmailNotificationsToggle(e.target.checked)}
                                            className='account-page-content-notifications-toggle-input'
                                        />
                                        <label htmlFor="email-notifications" className='account-page-content-notifications-toggle-label'>
                                            <span className='account-page-content-notifications-toggle-slider'></span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Map Style Section */}
                <div className='account-page-content-section' style={{ paddingBottom: '1rem' }}>
                    <div className='account-page-content-section-title'>Map Style</div>
                    <div className='account-page-content-map-style'>
                        <div className='account-page-content-map-style-description'>
                            Customize the appearance of the map with a style of your choice.
                        </div>
                        <div className='account-page-content-map-style-options'>
                            {mapStyles.map((style, index) => (
                                <div 
                                    key={index}
                                    className={`account-page-content-map-style-option ${
                                        config.map_style === style.link ? 'selected' : ''
                                    }`}
                                    onClick={() => handleMapStyleChange(style.link)}
                                >
                                    <div className='account-page-content-map-style-option-content'>
                                        <img 
                                            src={style.image} 
                                            alt={style.name}
                                            className='account-page-content-map-style-option-image'
                                        />
                                        <span className='account-page-content-map-style-option-name'>
                                            {style.name}
                                        </span>
                                    </div>
                                    <div className='account-page-content-map-style-option-radio'>
                                        <div className={`account-page-content-map-style-option-radio-indicator ${
                                            config.map_style === style.link ? 'checked' : ''
                                        }`}>
                                            {config.map_style === style.link && (
                                                <div className='account-page-content-map-style-option-radio-dot'></div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                 {/* Subscription Section */}
                 <div className='account-page-content-section'>
                    <div className='account-page-content-section-title'>Subscription Management</div>
                    <div className='account-page-content-subscription'>
                        <div className='account-page-content-profile-row'>
                            <WalletIcon size={18} />
                            <div className='account-page-content-subscription-info-text'>
                                Current Plan: <strong>{`${config.subscription_type.charAt(0).toUpperCase() + config.subscription_type.slice(1)}`}</strong>
                            </div>
                        </div>
                       {(config.subscription_type === 'annual' || config.subscription_type === 'monthly') && <button 
                            onClick={handleManageSubscription}
                            className='account-page-content-subscription-btn'>
                            Manage Subscription
                        </button>}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className='account-page'>
            {modal.type && 
            <AccountModal 
                modal={modal} 
                setModal={setModal}
                setEmail={setEmail}
                setPanel={setPanel}
            />}
            <div className='account-page-title'>
                {/* <User size={24} color="#db6300" weight="fill" /> */}
                <div className='account-page-title-text'>Account</div>
            </div>
            <div className='account-page-description'>Manage all your account details, payment methods, and subscription plans. Easily update personal information, payment methods, and subscription details.</div>
            {renderAccountContent()}
        </div>
    )
}